import React, { useCallback, useRef, useState, useEffect } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import { Divider } from '@material-ui/core';
import { IoIosArrowBack } from 'react-icons/io';

import { useToast } from '../../hooks/toast';
import { useAuth } from '../../hooks/auth';

import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErrors';

import Input from '../../newComponents/UnformInput';
import UnformFiscalCodeAutocomplete from '../../newComponents/UnformFiscalCodeAutocomplete';

import {
  buscarCstIbsCbs,
  buscarCClassTrib,
} from '../../services/fiscalCatalogService';
import Button from '../../newComponents/Button';

import * as S from './styles';

interface ReformaFormData {
  cst_ibs_cbs?: string;
  c_class_trib?: string;
  aliquota_ibs_uf?: string;
  aliquota_ibs_mun?: string;
  aliquota_cbs?: string;
}

// PATCH parcial: envia apenas os campos da reforma (não toca em ICMS/PIS/COFINS).
// String vazia vira null para limpar o campo.
function toPayload(data: ReformaFormData): Record<string, unknown> {
  const fields: (keyof ReformaFormData)[] = [
    'cst_ibs_cbs',
    'c_class_trib',
    'aliquota_ibs_uf',
    'aliquota_ibs_mun',
    'aliquota_cbs',
  ];

  return fields.reduce((acc, field) => {
    const value = data[field];
    acc[field] = value === '' || value === undefined ? null : value;
    return acc;
  }, {} as Record<string, unknown>);
}

const EditRegraIbsCbsIs: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user } = useAuth();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [descricao, setDescricao] = useState('');
  // CST escolhido: filtra a lista de cClassTrib. O valor que vai no submit é o
  // do unform; este estado é só o gatilho de recarga da lista.
  const [cstAtual, setCstAtual] = useState('');
  const [mostrarTodasClasses, setMostrarTodasClasses] = useState(false);

  useEffect(() => {
    async function loadGrupo(): Promise<void> {
      try {
        const response = await api.get(
          `companies/${user.empresa_id}/grupos-tributarios/${id}`,
        );
        setDescricao(response.data.descricao);
        setCstAtual(response.data.cst_ibs_cbs ?? '');
        formRef.current?.setData({
          cst_ibs_cbs: response.data.cst_ibs_cbs ?? '',
          c_class_trib: response.data.c_class_trib ?? '',
          aliquota_ibs_uf: response.data.aliquota_ibs_uf ?? '',
          aliquota_ibs_mun: response.data.aliquota_ibs_mun ?? '',
          aliquota_cbs: response.data.aliquota_cbs ?? '',
        });
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao carregar grupo' });
        history.push('/regras-ibs-cbs-is');
      } finally {
        setIsLoading(false);
      }
    }

    loadGrupo();
  }, [id, user.empresa_id, addToast, history]);

  const handleSubmit = useCallback(
    async (data: ReformaFormData) => {
      try {
        setIsLoading(true);
        formRef.current?.setErrors({});

        // Sem campos obrigatórios: todos os campos da reforma são opcionais.
        const schema = Yup.object().shape({});
        await schema.validate(data, { abortEarly: false });

        await api.patch(
          `companies/${user.empresa_id}/grupos-tributarios/${id}`,
          toPayload(data),
        );

        addToast({ type: 'success', title: 'Regras IBS/CBS/IS atualizadas' });
        history.push('/regras-ibs-cbs-is');
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          formRef.current?.setErrors(getValidationErrors(err));
        } else {
          addToast({
            type: 'error',
            title: 'Erro ao atualizar regras da reforma',
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [id, user.empresa_id, addToast, history],
  );

  return (
    <S.Container>
      <header>
        <button
          className="previous_page"
          type="button"
          onClick={() => history.push('/regras-ibs-cbs-is')}
        >
          <IoIosArrowBack size={20} />
          Voltar
        </button>
        <div>
          <h1>Regras IBS, CBS e IS</h1>
          <p>
            Grupo: <strong>{descricao || '...'}</strong>. Edite somente os
            campos da reforma tributária.
          </p>
        </div>
      </header>

      <Divider />

      <S.CustomForm ref={formRef} onSubmit={handleSubmit}>
        <div className="multi">
          <UnformFiscalCodeAutocomplete
            id="cst_ibs_cbs"
            name="cst_ibs_cbs"
            label="CST IBS/CBS:"
            placeholder="Código ou descrição. Ex.: 000 ou integral"
            loadOptions={buscarCstIbsCbs}
            loadOnOpen
            disabled={isLoading}
            onValueChange={setCstAtual}
          />
          <UnformFiscalCodeAutocomplete
            id="c_class_trib"
            name="c_class_trib"
            label="cClassTrib:"
            placeholder="Código ou descrição. Ex.: 000001"
            // A classificação só existe DENTRO de um CST — a lista recarrega
            // quando o CST muda, como o CEST faz com o NCM.
            loadOptions={(termo) =>
              buscarCClassTrib({
                cst: cstAtual,
                search: termo,
                apenasNfce: !mostrarTodasClasses,
              })
            }
            reloadKey={`${cstAtual}-${mostrarTodasClasses}`}
            loadOnOpen
            disabled={isLoading}
            helperText={
              mostrarTodasClasses
                ? 'Mostrando a tabela inteira do IT 2025.002, inclusive o que não vale para NFC-e.'
                : 'Mostrando só as classificações válidas para NFC-e.'
            }
          />
        </div>

        <button
          type="button"
          className="link_button"
          onClick={() => setMostrarTodasClasses((valor) => !valor)}
        >
          {mostrarTodasClasses
            ? 'Mostrar só o que vale para NFC-e'
            : 'Mostrar todas as classificações do IT 2025.002'}
        </button>
        <div className="multi">
          <Input
            name="aliquota_ibs_uf"
            label="Alíquota IBS UF (%):"
            type="number"
            step="0.0001"
            disabled={isLoading}
          />
          <Input
            name="aliquota_ibs_mun"
            label="Alíquota IBS Município (%):"
            type="number"
            step="0.0001"
            disabled={isLoading}
          />
          <Input
            name="aliquota_cbs"
            label="Alíquota CBS (%):"
            type="number"
            step="0.0001"
            disabled={isLoading}
          />
        </div>

        <div className="buttons">
          <Button full type="submit" loading={isLoading} disabled={isLoading}>
            Salvar
          </Button>
        </div>
      </S.CustomForm>
    </S.Container>
  );
};

export default EditRegraIbsCbsIs;
